import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Checkbox,
} from '../../components/ui/checkbox';
import {
  Mail,
  Inbox,
  Trash2,
  FileUp,
  RefreshCw,
  Search,
  Paperclip,
  Calendar,
  User,
  Eye,
  CheckCircle,
  XCircle,
  Loader2,
  AlertTriangle,
  BriefcaseBusiness,
} from 'lucide-react';
import { toast } from 'sonner';
import { CreateInquiryFromEmailDialog } from '../../components/inquiries/CreateInquiryFromEmailDialog';
import { StagingAttachmentPreview, isPreviewable } from '../../components/inquiries/StagingAttachmentPreview';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export default function EmailStagingInboxPage() {
  const navigate = useNavigate();
  const { token } = useAuth();
  const [emails, setEmails] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [stats, setStats] = useState({ total_pending: 0, total_imported: 0, total_deleted: 0 });
  const [filter, setFilter] = useState({ status: 'pending', account: 'all' });
  const [accounts, setAccounts] = useState([]);
  const [selectedEmail, setSelectedEmail] = useState(null);
  const [showImportModal, setShowImportModal] = useState(false);
  const [importData, setImportData] = useState({
    part_number: '',
    description: '',
    material: '',
    finish: '',
    quantity: '',
    unit_price: '',
    notes: '',
  });
  const [importing, setImporting] = useState(false);
  
  // Bulk selection state
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [refetchingId, setRefetchingId] = useState(null);
  const [promotingEmail, setPromotingEmail] = useState(null);
  const [previewing, setPreviewing] = useState(null);
  const [health, setHealth] = useState(null);
  const [bulkJob, setBulkJob] = useState(null);

  const fetchEmails = useCallback(async () => {
    try {
      const params = new URLSearchParams({
        status: filter.status,
        limit: '100',
      });
      if (filter.account !== 'all') {
        params.append('account', filter.account);
      }
      const response = await axios.get(`${API_URL}/staging-inbox?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setEmails(response.data.emails || []);
    } catch (error) {
      toast.error('Failed to load emails');
    } finally {
      setLoading(false);
    }
  }, [token, filter]);

  const fetchStats = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/staging-inbox-stats`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setStats(response.data);
    } catch (error) {
      console.error('Failed to fetch staging inbox stats', error);
    }
  }, [token]);

  const fetchAccounts = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/email-accounts`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setAccounts(response.data || []);
    } catch (error) {
      console.error('Failed to fetch email accounts', error);
    }
  }, [token]);

  useEffect(() => {
    fetchEmails();
    fetchStats();
    fetchAccounts();
    fetchHealth();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchEmails, fetchStats, fetchAccounts]);

  const handleSync = async () => {
    setSyncing(true);
    try {
      await axios.post(`${API_URL}/email-ingestion/sync`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Email sync started');
      // Poll for completion
      setTimeout(() => {
        fetchEmails();
        fetchStats();
        setSyncing(false);
      }, 5000);
    } catch (error) {
      toast.error('Failed to start sync');
      setSyncing(false);
    }
  };

  const handleDelete = async (emailId) => {
    if (!window.confirm('Delete this email from staging inbox?\n\nNote: This only removes the record from this app. The original email in Gmail/Outlook will NOT be deleted.')) return;
    try {
      await axios.delete(`${API_URL}/staging-inbox/${emailId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Email removed from staging (original email preserved in mailbox)');
      setSelectedIds(prev => {
        const newSet = new Set(prev);
        newSet.delete(emailId);
        return newSet;
      });
      fetchEmails();
      fetchStats();
    } catch (error) {
      toast.error('Failed to delete email');
    }
  };

  // Bulk delete selected emails
  const handleBulkDelete = async () => {
    if (selectedIds.size === 0) return;
    if (!window.confirm(`Delete ${selectedIds.size} selected email(s) from staging inbox?\n\nNote: This only removes records from this app. The original emails in Gmail/Outlook will NOT be deleted.`)) return;
    
    setBulkDeleting(true);
    let successCount = 0;
    let failCount = 0;
    
    for (const emailId of selectedIds) {
      try {
        await axios.delete(`${API_URL}/staging-inbox/${emailId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        successCount++;
      } catch (error) {
        failCount++;
      }
    }
    
    setBulkDeleting(false);
    setSelectedIds(new Set());
    
    if (failCount === 0) {
      toast.success(`${successCount} email(s) removed from staging`);
    } else {
      toast.warning(`${successCount} removed, ${failCount} failed`);
    }
    
    fetchEmails();
    fetchStats();
  };

  // Toggle select all
  const toggleSelectAll = () => {
    if (selectedIds.size === emails.filter(e => e.status === 'pending').length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(emails.filter(e => e.status === 'pending').map(e => e.id)));
    }
  };

  // Toggle single selection
  const toggleSelect = (emailId) => {
    setSelectedIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(emailId)) {
        newSet.delete(emailId);
      } else {
        newSet.add(emailId);
      }
      return newSet;
    });
  };

  const fetchHealth = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/staging-inbox/attachment-health`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setHealth(data);
    } catch { /* non-critical */ }
  };

  const pollBulkJob = async (jobId) => {
    for (let attempt = 0; attempt < 240; attempt += 1) {
      await new Promise((resolve) => setTimeout(resolve, 3000));
      const { data } = await axios.get(`${API_URL}/staging-inbox/refetch-jobs/${jobId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setBulkJob(data);
      if (data.status === 'completed') {
        toast.success(`${data.repaired} email(s) repaired · ${data.attachments_repaired} attachment(s) retained`, {
          description: data.failed ? `${data.failed} could not be recovered from the mailbox` : undefined,
        });
        await fetchEmails();
        await fetchHealth();
        return;
      }
    }
  };

  const handleBulkRefetch = async () => {
    try {
      const { data } = await axios.post(`${API_URL}/staging-inbox/refetch-all-attachments`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!data.job_id) {
        toast.info(data.message);
        await fetchHealth();
        return;
      }
      setBulkJob({ ...data, status: 'running', processed: 0, repaired: 0, attachments_repaired: 0, failed: 0 });
      toast.info(data.message, { description: 'This runs in the background — you can keep working.' });
      pollBulkJob(data.job_id);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not start the re-fetch');
    }
  };

  const handleInquiryCreated = async (result) => {
    await fetchEmails();
    await fetchStats();
    navigate(`/inquiries/${result.inquiry_id}`);
  };

  const downloadAttachment = async (emailId, index, filename) => {
    try {
      const response = await axios.get(`${API_URL}/staging-inbox/${emailId}/attachment/${index}`, {
        headers: { Authorization: `Bearer ${token}` }, responseType: 'blob',
      });
      const url = URL.createObjectURL(response.data);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename || 'attachment';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      toast.error(error.response?.status === 410
        ? 'This attachment was not retained — use "Re-fetch attachments"'
        : 'Could not download this attachment');
    }
  };

  const refetchAttachments = async (emailId) => {
    setRefetchingId(emailId);
    try {
      const response = await axios.post(`${API_URL}/staging-inbox/${emailId}/refetch-attachments`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const retained = (response.data.attachments || []).filter((item) => item.retained).length;
      toast.success(`${retained} attachment(s) pulled from the mailbox and retained`);
      await fetchEmails();
      setSelectedEmail(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not re-fetch the attachments');
    } finally { setRefetchingId(null); }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-start">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Email Inbox</h1>
          <p className="text-slate-500 mt-1">
            Review incoming customer emails and turn genuine RFQs into inquiries, with their attachments filed.
          </p>
          <p className="text-xs text-slate-400 mt-1">
            Auto-syncs every 2 hours. Last sync: {emails.length > 0 
              ? new Date(Math.max(...emails.map(e => new Date(e.synced_at || e.received_date || 0)))).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })
              : 'No emails yet'}
          </p>
        </div>
        <Button
          onClick={handleSync}
          disabled={syncing}
          className="bg-blue-600 hover:bg-blue-700"
          data-testid="sync-emails-btn"
        >
          {syncing ? (
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
          ) : (
            <RefreshCw className="w-4 h-4 mr-2" />
          )}
          {syncing ? 'Syncing...' : 'Sync Now'}
        </Button>
      </div>

      {(health?.emails_with_missing_attachments > 0 || bulkJob) && (
        <div className="flex flex-col gap-2 border border-amber-300 bg-amber-50 p-4 text-sm text-amber-900 sm:flex-row sm:items-center sm:justify-between"
          data-testid="bulk-refetch-banner">
          <span>
            {bulkJob && bulkJob.status === 'running'
              ? `Re-fetching attachments — ${bulkJob.processed || 0} of ${bulkJob.total} checked, ${bulkJob.repaired || 0} repaired`
              : bulkJob && bulkJob.status === 'completed'
                ? `${bulkJob.repaired} email(s) repaired · ${bulkJob.attachments_repaired} attachment(s) retained${bulkJob.failed ? ` · ${bulkJob.failed} not recoverable` : ''}`
                : `${health.emails_with_missing_attachments} staged email(s) have attachments that were never retained. Pull them back from the mailboxes.`}
          </span>
          <Button size="sm" onClick={handleBulkRefetch}
            disabled={bulkJob?.status === 'running'} data-testid="bulk-refetch-button">
            {bulkJob?.status === 'running' ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
            Re-fetch missing attachments
          </Button>
        </div>
      )}

      {/* Stats Cards */}
      <div className="grid grid-cols-4 gap-4">
      <Card className="border-l-4 border-l-amber-500">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-slate-500 uppercase font-medium">Pending Review</p>
                <p className="text-2xl font-bold text-amber-600">{stats.total_pending}</p>
              </div>
              <Inbox className="w-8 h-8 text-amber-500 opacity-50" />
            </div>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-green-500">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-slate-500 uppercase font-medium">Imported</p>
                <p className="text-2xl font-bold text-green-600">{stats.total_imported}</p>
              </div>
              <CheckCircle className="w-8 h-8 text-green-500 opacity-50" />
            </div>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-red-500">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-slate-500 uppercase font-medium">Deleted</p>
                <p className="text-2xl font-bold text-red-600">{stats.total_deleted}</p>
              </div>
              <XCircle className="w-8 h-8 text-red-500 opacity-50" />
            </div>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-blue-500">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-slate-500 uppercase font-medium">Accounts</p>
                <p className="text-2xl font-bold text-blue-600">{accounts.length}</p>
              </div>
              <Mail className="w-8 h-8 text-blue-500 opacity-50" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-4">
          <div className="flex gap-4 items-center">
            <div className="flex-1">
              <Select
                value={filter.status}
                onValueChange={(v) => setFilter({ ...filter, status: v })}
              >
                <SelectTrigger data-testid="status-filter">
                  <SelectValue placeholder="Filter by status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="pending">Pending Review</SelectItem>
                  <SelectItem value="imported">Imported</SelectItem>
                  <SelectItem value="deleted">Deleted</SelectItem>
                  <SelectItem value="all">All</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex-1">
              <Select
                value={filter.account}
                onValueChange={(v) => setFilter({ ...filter, account: v })}
              >
                <SelectTrigger data-testid="account-filter">
                  <SelectValue placeholder="Filter by account" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Accounts</SelectItem>
                  {accounts.map((acc) => (
                    <SelectItem key={acc.id} value={acc.email}>
                      {acc.email}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Button variant="outline" onClick={fetchEmails}>
              <Search className="w-4 h-4 mr-2" />
              Apply Filters
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Email List */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Inbox className="w-5 h-5" />
                Staging Inbox
              </CardTitle>
              <CardDescription>
                {emails.length} emails found. Open one to review it, then create an inquiry with its attachments.
              </CardDescription>
            </div>
            {/* Bulk Actions */}
            {selectedIds.size > 0 && (
              <div className="flex items-center gap-2">
                <span className="text-sm text-slate-500">
                  {selectedIds.size} selected
                </span>
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={handleBulkDelete}
                  disabled={bulkDeleting}
                  data-testid="bulk-delete-btn"
                >
                  {bulkDeleting ? (
                    <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                  ) : (
                    <Trash2 className="w-4 h-4 mr-1" />
                  )}
                  Delete Selected
                </Button>
              </div>
            )}
          </div>
          {/* Safety Notice */}
          <div className="flex items-center gap-2 mt-2 p-2 bg-amber-50 rounded-md text-xs text-amber-700">
            <AlertTriangle className="w-4 h-4" />
            <span>Deleting emails here only removes them from this app. Original emails in Gmail/Outlook are preserved.</span>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
            </div>
          ) : emails.length === 0 ? (
            <div className="text-center py-12 text-slate-500">
              <Inbox className="w-12 h-12 mx-auto mb-4 opacity-30" />
              <p>No emails found matching your filters</p>
            </div>
          ) : (
            <div className="divide-y">
              {/* Select All Header */}
              {emails.filter(e => e.status === 'pending').length > 0 && (
                <div className="py-2 px-4 -mx-4 bg-slate-50 flex items-center gap-2">
                  <Checkbox
                    checked={selectedIds.size === emails.filter(e => e.status === 'pending').length && selectedIds.size > 0}
                    onCheckedChange={toggleSelectAll}
                    data-testid="select-all-checkbox"
                  />
                  <span className="text-sm text-slate-500">Select all pending</span>
                </div>
              )}
              {emails.map((email) => (
                <div
                  key={email.id}
                  className="py-4 hover:bg-slate-50 px-4 -mx-4"
                  data-testid={`email-row-${email.id}`}
                >
                  <div className="flex items-start gap-3">
                    {/* Checkbox for pending emails only */}
                    {email.status === 'pending' && (
                      <Checkbox
                        checked={selectedIds.has(email.id)}
                        onCheckedChange={() => toggleSelect(email.id)}
                        onClick={(e) => e.stopPropagation()}
                        className="mt-1"
                        data-testid={`select-email-${email.id}`}
                      />
                    )}
                    <div 
                      className="flex-1 min-w-0 cursor-pointer"
                      onClick={() => setSelectedEmail(email)}
                    >
                      <div className="flex items-center gap-2">
                        <h4 className="font-medium text-slate-900 truncate">
                          {email.subject || '(No Subject)'}
                        </h4>
                        <Badge
                          variant={
                            email.status === 'pending'
                              ? 'outline'
                              : email.status === 'imported'
                              ? 'default'
                              : 'destructive'
                          }
                          className="text-xs"
                        >
                          {email.status}
                        </Badge>
                      </div>
                      <div className="flex items-center gap-4 mt-1 text-sm text-slate-500">
                        <span className="flex items-center gap-1">
                          <User className="w-3 h-3" />
                          {email.sender_name || email.sender_email}
                        </span>
                        <span className="flex items-center gap-1">
                          <Calendar className="w-3 h-3" />
                          {formatDate(email.received_at)}
                        </span>
                        {email.attachments?.length > 0 && (
                          <span className="flex items-center gap-1">
                            <Paperclip className="w-3 h-3" />
                            {email.attachments.length} attachment(s)
                            {email.attachments.some((att) => !att.storage_path) && (
                              <span className="text-amber-700" data-testid={`attachments-not-retained-${email.id}`}>· not retained</span>
                            )}
                          </span>
                        )}
                      </div>
                      {email.inquiry_verdict && (
                        <div className="mt-2 flex flex-wrap items-center gap-2 text-xs">
                          <Badge
                            variant="outline"
                            className={email.inquiry_verdict.likely_inquiry
                              ? 'border-emerald-300 bg-emerald-50 text-emerald-800'
                              : 'border-slate-200 text-slate-500'}
                            data-testid={`inquiry-verdict-${email.id}`}
                          >
                            {email.inquiry_verdict.likely_inquiry ? 'Likely inquiry' : 'Probably not an inquiry'}
                          </Badge>
                          {(email.inquiry_verdict.reasons || []).map((reason) => (
                            <span key={reason} className="text-slate-500">{reason}</span>
                          ))}
                        </div>
                      )}
                      <p className="mt-2 text-sm text-slate-600 line-clamp-2">
                        {email.body_text?.slice(0, 200) || '(No content)'}
                      </p>
                    </div>
                    <div className="flex gap-2 ml-4">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedEmail(email);
                        }}
                        data-testid={`view-email-${email.id}`}
                      >
                        <Eye className="w-4 h-4" />
                      </Button>
                      {email.status === 'pending' && (
                        <>
                          <Button size="sm" className="bg-emerald-700 hover:bg-emerald-800" onClick={(event) => {
                            event.stopPropagation(); setPromotingEmail(email);
                          }} disabled={importing} data-testid={`promote-email-to-inquiry-${email.id}`}>
                            <BriefcaseBusiness className="mr-1 h-4 w-4" /><span className="hidden sm:inline">Create Inquiry</span>
                          </Button>
                          <Button
                            size="sm"
                            variant="destructive"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDelete(email.id);
                            }}
                            data-testid={`delete-email-${email.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Email Detail Modal */}
      <Dialog open={!!selectedEmail && !showImportModal} onOpenChange={() => setSelectedEmail(null)}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{selectedEmail?.subject || '(No Subject)'}</DialogTitle>
            <DialogDescription>
              From: {selectedEmail?.sender_name} &lt;{selectedEmail?.sender_email}&gt;
              <br />
              Received: {formatDate(selectedEmail?.received_at)}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="bg-slate-50 p-4 rounded-lg">
              <h4 className="font-medium mb-2">Email Body</h4>
              <pre className="text-sm whitespace-pre-wrap font-sans text-slate-700">
                {selectedEmail?.body_text || '(No content)'}
              </pre>
            </div>
            {selectedEmail?.attachments?.length > 0 && (
              <div>
                <h4 className="font-medium mb-2">Attachments</h4>
                <div className="space-y-2">
                  {selectedEmail.attachments.map((att, idx) => (
                    <div key={att.filename || idx} className="flex items-center gap-2 p-2 bg-slate-100 rounded"
                      data-testid={`staging-attachment-${idx}`}>
                      <Paperclip className="w-4 h-4 text-slate-500" />
                      <span className="text-sm flex-1 truncate">{att.filename}</span>
                      <Badge variant="outline" className="text-xs">{att.mime_type}</Badge>
                      {att.storage_path ? (
                        <>
                          {isPreviewable(att.filename) && (
                            <Button size="sm" variant="outline"
                              onClick={() => setPreviewing({ ...att, index: idx, emailId: selectedEmail.id })}
                              data-testid={`preview-staging-attachment-${idx}`}>Preview</Button>
                          )}
                          <Button size="sm" variant="outline"
                            onClick={() => downloadAttachment(selectedEmail.id, idx, att.filename)}
                            data-testid={`download-staging-attachment-${idx}`}>Download</Button>
                        </>
                      ) : (
                        <Badge variant="outline" className="border-amber-300 bg-amber-50 text-xs text-amber-800"
                          data-testid={`staging-attachment-missing-${idx}`}>not retained</Badge>
                      )}
                    </div>
                  ))}
                </div>
                {selectedEmail.attachments.some((att) => !att.storage_path) && (
                  <div className="mt-3 flex flex-col gap-2 border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900 sm:flex-row sm:items-center sm:justify-between"
                    data-testid="staging-refetch-banner">
                    <span>Some attachments were not retained when this email was synced. Pull them from the mailbox again.</span>
                    <Button size="sm" onClick={() => refetchAttachments(selectedEmail.id)}
                      disabled={refetchingId === selectedEmail.id} data-testid="refetch-attachments-button">
                      {refetchingId === selectedEmail.id ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                      Re-fetch attachments
                    </Button>
                  </div>
                )}
              </div>
            )}
          </div>
          <DialogFooter>
            {selectedEmail?.status === 'pending' && (
              <>
                <Button variant="outline" onClick={() => setSelectedEmail(null)}>
                  Close
                </Button>
                <Button className="bg-emerald-700 hover:bg-emerald-800"
                  onClick={() => { setPromotingEmail(selectedEmail); setSelectedEmail(null); }}
                  disabled={importing} data-testid="promote-selected-email-to-inquiry-button">
                  <BriefcaseBusiness className="mr-2 h-4 w-4" /> Create Inquiry
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <StagingAttachmentPreview
        emailId={previewing?.emailId}
        attachment={previewing}
        open={Boolean(previewing)}
        onOpenChange={(value) => !value && setPreviewing(null)}
      />

      <CreateInquiryFromEmailDialog
        email={promotingEmail}
        open={Boolean(promotingEmail)}
        onOpenChange={(value) => !value && setPromotingEmail(null)}
        onCreated={handleInquiryCreated}
      />
    </div>
  );
}
